import { useCallback } from 'react'
import {
  useMutation,
  ApolloError,
  type FetchResult,
  type OperationVariables,
  type ErrorPolicy,
  type DocumentNode,
} from '@apollo/client'
import { emitGraphQLError } from './errors'
import { extractOperationInfo } from './utils'
import {
  extractEntityIdFromVariables,
  extractFieldsFromVariables,
  createAggregatedMutation,
} from './utils'
import type {
  UseGraphQLMutationProps,
  GraphQLMutationResult,
  GraphQLMutationExecuteOptions,
} from './types'

/**
 * React hook for executing GraphQL mutations with automatic data refetching.
 * Combines mutation and get operations into a single request for optimal performance.
 */
export const useGraphQLMutation = <
  TData = unknown,
  TVariables extends OperationVariables = OperationVariables,
>({
  mutation,
  resourceName,
}: UseGraphQLMutationProps): GraphQLMutationResult<TData, TVariables> => {
  if (!mutation) {
    throw new Error('useGraphQLMutation: mutation is required')
  }
  if (!resourceName || typeof resourceName !== 'string') {
    throw new Error(
      'useGraphQLMutation: resourceName must be a non-empty string',
    )
  }

  const [apolloMutate, { data, loading, error, reset }] = useMutation<
    TData,
    TVariables
  >(mutation)

  const mutate = useCallback(
    async (
      options: GraphQLMutationExecuteOptions<TVariables> = {},
    ): Promise<FetchResult<TData>> => {
      try {
        const { name: queryName } = extractOperationInfo(
          mutation as unknown as DocumentNode,
        )
        const hasExplicitVariables =
          'variables' in options && options.variables !== undefined
        const variables = hasExplicitVariables
          ? options.variables
          : (options as TVariables)
        const executeOptions = hasExplicitVariables
          ? { ...options, variables: options.variables }
          : { variables }

        const entityId = extractEntityIdFromVariables(
          variables as OperationVariables,
        )

        if (variables && entityId) {
          const fields = extractFieldsFromVariables(
            variables as OperationVariables,
          )
          const {
            mutation: aggregatedMutation,
            variables: aggregatedVariables,
          } = createAggregatedMutation(mutation, resourceName, entityId, fields)

          const result = await apolloMutate({
            ...executeOptions,
            // Ensure partial data is returned alongside errors
            errorPolicy:
              (executeOptions as { errorPolicy?: ErrorPolicy }).errorPolicy ??
              'all',
            mutation: aggregatedMutation,
            variables: { ...variables, ...aggregatedVariables } as TVariables,
          })
          if (result.errors && result.errors.length > 0)
            emitGraphQLError(
              new ApolloError({ graphQLErrors: result.errors }),
              {
                operation: 'mutation',
                queryName,
              },
            )
          return result
        } else {
          const result = await apolloMutate({
            ...executeOptions,
            // Ensure partial data is returned alongside errors
            errorPolicy:
              (executeOptions as { errorPolicy?: ErrorPolicy }).errorPolicy ??
              'all',
          })
          if (result.errors && result.errors.length > 0)
            emitGraphQLError(
              new ApolloError({ graphQLErrors: result.errors }),
              {
                operation: 'mutation',
                queryName,
              },
            )
          return result
        }
      } catch (err) {
        throw err as ApolloError
      }
    },
    [apolloMutate, mutation, resourceName],
  )

  return {
    mutate,
    data: data || null,
    loading,
    error: error || null,
    reset,
  }
}
